import { useState, useRef, useCallback, useEffect } from 'react';
import { IoSend, IoAirplaneSharp } from 'react-icons/io5';

type Props = {
  onUnlocked?: () => void;
  onProgress?: (progress: number) => void; // 0..1
};

export default function SubscribeSlider({ onUnlocked, onProgress }: Props) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [dragPosition, setDragPosition] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const maxDragRef = useRef(0);

  const UNLOCK_THRESHOLD = 1; // 100% нужно провести для разблокировки
  const BUTTON_WIDTH = 64; // w-16 = 64px

  const handleMove = useCallback((clientX: number) => {
    if (!isDraggingRef.current || !sliderRef.current) return;

    const sliderRect = sliderRef.current.getBoundingClientRect();
    const relativeX = clientX - sliderRect.left - BUTTON_WIDTH / 2;
    const newPosition = Math.max(0, Math.min(relativeX, maxDragRef.current));
    
    setDragPosition(newPosition);
    const progress = Math.max(0, Math.min(newPosition / (maxDragRef.current || 1), 1));
    onProgress?.(progress);

    // Проверяем разблокировку
    if (newPosition / maxDragRef.current >= UNLOCK_THRESHOLD) {
      setDragPosition(maxDragRef.current);
      isDraggingRef.current = false;
      onProgress?.(1);
      setIsUnlocked(true);
      onUnlocked?.();
    }
  }, []);

  const handleStart = useCallback((clientX: number) => {
    if (isUnlocked || !sliderRef.current) return;

    const sliderRect = sliderRef.current.getBoundingClientRect();
    maxDragRef.current = sliderRect.width - BUTTON_WIDTH;
    startXRef.current = clientX;
    isDraggingRef.current = true;
  }, [isUnlocked]);

  const handleEnd = useCallback(() => {
    if (!isUnlocked && isDraggingRef.current) {
      setDragPosition(0);
      onProgress?.(0);
    }
    isDraggingRef.current = false;
  }, [isUnlocked]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    handleStart(e.clientX);
  }, [handleStart]);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    handleStart(e.touches[0].clientX);
  }, [handleStart]);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (isDraggingRef.current) {
        handleMove(e.clientX);
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      if (isDraggingRef.current) {
        e.preventDefault();
        handleMove(e.touches[0].clientX);
      }
    };
    const onEnd = () => handleEnd();

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchend', onEnd);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchend', onEnd);
    };
  }, [handleMove, handleEnd]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const response = await fetch('/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setStatus('success');
        setEmail('');
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  const resetSlider = () => {
    setIsUnlocked(false);
    setDragPosition(0);
    setStatus('idle');
    setEmail('');
  };

  return (
    <div className="max-w-md mx-auto">
      {!isUnlocked ? (
        <div
          ref={sliderRef}
          className={`relative h-16 bg-white border border-gray-300 rounded-full overflow-hidden select-none`}
          style={{ touchAction: 'none' }}
        >
          {/* Фоновый текст */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span
              className="text-gray-400 font-medium transition-opacity duration-300"
              style={{ opacity: 1 - dragPosition / 200 }}
            >
              Unlock Your Journey →
            </span>
          </div>

          {/* Прогресс фон */}
          <div
            className="absolute left-0 top-0 h-full bg-waybook-green rounded-full"
            style={{ 
              width: `${dragPosition + BUTTON_WIDTH}px`,
              transition: dragPosition === 0 ? 'width 0.3s ease-out' : 'none'
            }}
          />

          {/* Кнопка с самолетиком */}
          <div
            ref={buttonRef}
            className="absolute left-0 top-0 h-15.5 w-16 bg-black rounded-full cursor-grab active:cursor-grabbing flex items-center justify-center shadow-lg z-10"
            style={{
              transform: `translateX(${dragPosition}px)`,
              transition: dragPosition === 0 ? 'transform 0.3s ease-out' : 'none',
              willChange: 'transform',
            }}
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
          >
            <IoAirplaneSharp className="w-6 h-6 text-waybook-green" />
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="relative">
          <div className="relative">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Leave your email to be the first"
              required
              autoFocus
              disabled={status === 'loading' || status === 'success'}
              className="w-full h-16 pl-6 pr-20 text-lg border border-gray-300 bg-white rounded-full focus:outline-none focus:ring-2 focus:ring-black disabled:opacity-50 placeholder-gray-400"
            />
            <button
              type="submit"
              disabled={status === 'loading' || status === 'success'}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-12 w-12 bg-waybook-green rounded-full flex items-center justify-center hover:bg-waybook-green-600 disabled:opacity-50 transition-colors"
            >
              {status === 'loading' ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <IoSend className="w-5 h-5 text-black" />
              )}
            </button>
          </div>

          {status === 'success' && (
            <div className="mt-4 text-center">
              <p className="text-green-600 font-medium">Successfully subscribed! ✈️</p>
              <button
                onClick={resetSlider}
                className="mt-2 text-sm text-gray-600 hover:text-black underline"
              >
                Subscribe another email
              </button>
            </div>
          )}
          {status === 'error' && (
            <div className="mt-4 text-center">
              <p className="text-red-600 font-medium">Something went wrong. Please try again.</p>
              <button
                onClick={resetSlider}
                className="mt-2 text-sm text-gray-600 hover:text-black underline"
              >
                Try again
              </button>
            </div>
          )}
        </form>
      )}
    </div>
  );
}
